import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PublicShell, SignupCta, useSeo } from "./glossary/PublicShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TOPICS = [
  { value: "general", label: "General question" },
  { value: "billing", label: "Billing & account" },
  { value: "technical", label: "Technical issue" },
  { value: "partnership", label: "Partnership / media" },
  { value: "privacy", label: "Privacy request" },
];

export default function Contact() {
  useSeo({
    title: "Contact KidPath — questions & support",
    description: "Get in touch with the KidPath team. Ask about funding navigation, your account, partnerships, or privacy — we usually reply within a couple of business days.",
  });

  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.message.trim().length < 5) {
      toast.error("Please enter your name and a short message."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <PublicShell>
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-14 sm:py-20">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3">
            <MessageCircle size={16} /> We're here to help
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-kid-navy leading-tight">Get in touch</h1>
          <p className="text-kid-muted mt-4 text-base leading-relaxed">
            Have a question about your account or our platform? Send us a note and a real person on the KidPath team will get back to you — usually within a couple of business days.
          </p>
          <a href="mailto:hello@kidpath.ca" className="inline-flex items-center gap-2 text-kid-teal font-medium mt-4 hover:underline" data-testid="contact-email-link">
            <Mail size={16} /> hello@kidpath.ca
          </a>
        </div>

        <div className="mt-10 max-w-3xl">
          {sent ? (
            <div className="rounded-3xl border border-kid-line bg-kid-skytint/40 p-8 text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-2xl bg-kid-teal grid place-items-center mx-auto mb-4">
                <CheckCircle2 size={26} className="text-white" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-kid-navy">Message sent!</h2>
              <p className="text-kid-muted mt-2 max-w-md mx-auto">
                Thanks for reaching out. We've emailed you a copy and our team will reply as soon as we can.
              </p>
              <Button className="mt-6" onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "general", message: "", company: "" }); }} data-testid="contact-send-another">
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-kid-line bg-white p-6 sm:p-8 space-y-5" data-testid="contact-form">
              {/* Honeypot */}
              <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)}
                className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-kid-navy">Your name</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jordan Smith" className="mt-1.5 h-11 bg-white" data-testid="contact-name" required />
                </div>
                <div>
                  <Label className="text-sm text-kid-navy">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1.5 h-11 bg-white" data-testid="contact-email" required />
                </div>
              </div>
              <div>
                <Label className="text-sm text-kid-navy">What's this about?</Label>
                <Select value={form.topic} onValueChange={(v) => set("topic", v)}>
                  <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="contact-topic"><SelectValue /></SelectTrigger>
                  <SelectContent>{TOPICS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-kid-navy">Message</Label>
                <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={6} placeholder="Tell us how we can help…" className="mt-1.5 bg-white" data-testid="contact-message" required />
              </div>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto rounded-full px-8 h-12 text-base" data-testid="contact-submit">
                {submitting ? "Sending…" : "Send message"}
              </Button>
              <p className="text-xs text-kid-muted">
                KidPath provides general information and navigation only — not medical, legal, tax, or financial advice.
              </p>
            </form>
          )}
        </div>

        <div className="mt-12 max-w-3xl">
          <SignupCta variant="inline" testid="contact-cta" />
        </div>
      </section>
    </PublicShell>
  );
}
